import { Button, LuxModal, LuxModalBody, LuxModalHeader } from '@components/ui';
import { ToastContainer } from '@components/ui/Toast';
import { ClipboardDocumentIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';

import { usePermissions } from '@/contexts/PermissionsContext';
import { useToast } from '@/hooks/useToast';
import { invoiceService, type PendingPeppolInvoice } from '@/services/invoiceService';

const PEPPOL_MODAL_QUERY_KEY = ['peppol-pending-invoices'];
export const OPEN_PEPPOL_MODAL_EVENT = 'open-peppol-modal';

const formatPrice = (price: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);

const formatDate = (isoDate: string) => {
  if (!isoDate) return '';
  return new Date(isoDate).toLocaleDateString('fr-FR');
};

const InvoiceCard: React.FC<{
  invoice: PendingPeppolInvoice;
  onCopyDesignation: (designation: string) => void;
  onCopyVatNumber: (vatNumber: string) => void;
  onCopyEmail: (email: string) => void;
  onMarkBilled: (invoiceId: string) => void;
  isMarking: boolean;
}> = ({ invoice, onCopyDesignation, onCopyVatNumber, onCopyEmail, onMarkBilled, isMarking }) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="text-sm font-semibold text-gray-800">
          Facture {invoice.invoice_number}
          {invoice.order_number && (
            <span className="font-normal text-gray-500"> — BDC {invoice.order_number}</span>
          )}{' '}
          <span className="font-normal text-gray-500">— {formatDate(invoice.invoice_date)}</span>
        </div>
        <Button
          variant="secondary"
          className="!bg-emerald-100 !text-emerald-800 !border-emerald-300 hover:!bg-emerald-200 text-xs px-3 py-1.5"
          onClick={() => onMarkBilled(invoice.id)}
          disabled={isMarking}
        >
          {isMarking ? 'Traitement…' : 'Facturée dans Billit'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5">
        {/* Colonne 1 : client */}
        <div className="md:col-span-2 p-4 border-b md:border-b-0 md:border-r border-gray-200 bg-white">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Client
          </div>
          <div className="text-sm text-gray-900 font-medium uppercase">{invoice.client.name}</div>
          <div className="text-sm text-gray-600 uppercase">{invoice.client.address}</div>
          <div className="text-sm text-gray-600 uppercase">
            {invoice.client.postal_code} {invoice.client.city}
          </div>
          <div className="text-sm text-gray-600 uppercase">{invoice.client.country}</div>
          {invoice.client.email && (
            <div className="text-sm text-gray-600 mt-1 flex items-center gap-1.5">
              {invoice.client.email}
              <button
                type="button"
                onClick={() => onCopyEmail(invoice.client.email)}
                title="Copier l'email"
                className="text-gray-400 hover:text-blue-600 transition-colors"
              >
                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {invoice.client.vat_number && (
            <div className="text-sm text-gray-600 mt-1 flex items-center gap-1.5">
              TVA : {invoice.client.vat_number}
              <button
                type="button"
                onClick={() => onCopyVatNumber(invoice.client.vat_number)}
                title="Copier le numéro de TVA (sans points)"
                className="text-gray-400 hover:text-blue-600 transition-colors"
              >
                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {invoice.payments.length > 0 && (
            <div className="text-sm text-gray-600 mt-1 space-y-0.5">
              {invoice.payments.map((payment, index) => (
                <div key={index}>
                  Paiement : {payment.method} — {formatPrice(payment.amount)} —{' '}
                  {formatDate(payment.payment_date)}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Colonne 2 : produits */}
        <div className="md:col-span-3 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                <th className="text-left px-4 py-2 font-semibold">Produit</th>
                <th className="text-right px-3 py-2 font-semibold">P.U.</th>
                <th className="text-right px-3 py-2 font-semibold">Qté</th>
                <th className="text-right px-4 py-2 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map(item => (
                <tr
                  key={item.id}
                  className="border-t border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => onCopyDesignation(item.designation)}
                  title="Cliquer pour copier la dénomination du produit"
                >
                  <td className="px-4 py-2 text-gray-900 uppercase flex items-center gap-1.5">
                    <ClipboardDocumentIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    {item.designation}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">
                    {formatPrice(item.unit_price)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">{item.quantity}</td>
                  <td className="px-4 py-2 text-right text-gray-900 font-medium">
                    {formatPrice(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const PeppolPendingInvoicesModal: React.FC = () => {
  const { isSuperAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const hasAutoOpenedRef = useRef(false);

  const { data: pendingInvoices = [] } = useQuery({
    queryKey: PEPPOL_MODAL_QUERY_KEY,
    queryFn: () => invoiceService.getPendingPeppolInvoices(),
    enabled: isSuperAdmin,
    staleTime: 60000,
  });

  // Ouverture automatique une fois à l'ouverture de l'app s'il y a des factures en attente.
  useEffect(() => {
    if (!isSuperAdmin || hasAutoOpenedRef.current) return;
    if (pendingInvoices.length > 0) {
      hasAutoOpenedRef.current = true;
      setIsOpen(true);
    }
  }, [isSuperAdmin, pendingInvoices.length]);

  // Ouverture forcée (ex: clic sur "Facturer" d'un BDC déjà verrouillé).
  useEffect(() => {
    if (!isSuperAdmin) return;
    const handler = () => setIsOpen(true);
    window.addEventListener(OPEN_PEPPOL_MODAL_EVENT, handler);
    return () => window.removeEventListener(OPEN_PEPPOL_MODAL_EVENT, handler);
  }, [isSuperAdmin]);

  const markBilledMutation = useMutation({
    mutationFn: (invoiceId: string) => invoiceService.markInvoiceBilledExternally(invoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PEPPOL_MODAL_QUERY_KEY });
      toast.success('Facture marquée comme facturée dans Billit');
    },
    onError: () => {
      toast.error('Erreur lors de la mise à jour de la facture');
    },
  });

  const handleCopyDesignation = async (designation: string) => {
    const uppercased = designation.toUpperCase();
    try {
      await navigator.clipboard.writeText(uppercased);
      toast.success(`« ${uppercased} » copié`);
    } catch {
      toast.error('Impossible de copier dans le presse-papier');
    }
  };

  const handleCopyVatNumber = async (vatNumber: string) => {
    const cleaned = vatNumber.replace(/\./g, '');
    try {
      await navigator.clipboard.writeText(cleaned);
      toast.success(`« ${cleaned} » copié`);
    } catch {
      toast.error('Impossible de copier dans le presse-papier');
    }
  };

  const handleCopyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`« ${email} » copié`);
    } catch {
      toast.error('Impossible de copier dans le presse-papier');
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <>
      <LuxModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        animationState={isOpen ? 'in' : null}
        size="full"
        className="!max-w-[66vw]"
        closeOnBackdrop
        closeOnEscape
      >
        <LuxModalHeader variant="default" showCloseButton onClose={() => setIsOpen(false)}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <DocumentTextIcon className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink">Factures à établir via Peppol</h3>
              <p className="text-xs text-muted mt-0.5">
                {pendingInvoices.length} facture{pendingInvoices.length > 1 ? 's' : ''} à créer
                manuellement dans Billit
              </p>
            </div>
          </div>
        </LuxModalHeader>
        <LuxModalBody className="max-h-[75vh] overflow-y-auto">
          {pendingInvoices.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-10">
              Aucune facture en attente d'établissement.
            </div>
          ) : (
            <div className="space-y-4">
              {pendingInvoices.map(invoice => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  onCopyDesignation={handleCopyDesignation}
                  onCopyVatNumber={handleCopyVatNumber}
                  onCopyEmail={handleCopyEmail}
                  onMarkBilled={id => markBilledMutation.mutate(id)}
                  isMarking={
                    markBilledMutation.isPending && markBilledMutation.variables === invoice.id
                  }
                />
              ))}
            </div>
          )}
        </LuxModalBody>
      </LuxModal>

      <ToastContainer toasts={toast.toasts} onClose={toast.removeToast} />
    </>
  );
};

export default PeppolPendingInvoicesModal;
